import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import RelatedPages from '@/components/base/RelatedPages';

const productTypes = [
  '膠囊（硬膠囊）',
  '軟膠囊',
  '錠劑',
  '咀嚼錠',
  '粉末包／顆粒',
  '機能飲品',
  '口服液',
  '軟糖',
  '其他劑型（尚未確定）',
];

const quantityOptions = [
  '3,000 瓶以下（樣品 / 試製）',
  '3,000–10,000 瓶',
  '10,000–30,000 瓶',
  '30,000 瓶以上',
  '尚未確定，需要建議',
];

const suitableFor = [
  { icon: 'ri-store-2-line', title: '新創保健品牌', desc: '想從零開始建立自有品牌，尋找可配合少量起訂的代工夥伴。' },
  { icon: 'ri-shopping-bag-line', title: '電商 / 零售通路商', desc: '有穩定通路，想拓展自有品牌產品線或代理品項。' },
  { icon: 'ri-global-line', title: '有意出口的品牌', desc: '計畫進攻海外市場，需要具備出口文件支援能力的代工廠。' },
  { icon: 'ri-team-line', title: '需要研發支援的品牌', desc: '有產品概念但缺乏配方技術，需要 ODM 研發設計服務。' },
];

const processReminders = [
  { step: '01', icon: 'ri-send-plane-line', text: '填寫表單或加入 LINE，業務一個工作天內回覆' },
  { step: '02', icon: 'ri-discuss-line', text: '深度溝通需求，確認產品方向與合作可行性' },
  { step: '03', icon: 'ri-draft-line', text: '提供配方建議書與初步報價' },
  { step: '04', icon: 'ri-test-tube-line', text: '試製樣品，確認品質後正式進入合作' },
];

const FORM_URL = 'https://readdy.ai/api/form/d7kd2a263r16okiqphkg';

export default function InquiryContent() {
  const [submitState, setSubmitState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="requirement"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setSubmitState('loading');
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitState('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitState('error');
      }
    } catch {
      setSubmitState('error');
    }
  };

  return (
    <>
      {/* Hero Banner */}
      <section className="relative pt-20 min-h-[340px] md:min-h-[400px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20business%20consultation%20handshake%20meeting%20health%20supplement%20brand%20partnership%2C%20modern%20bright%20office%20with%20green%20plants%2C%20warm%20welcoming%20atmosphere%2C%20clean%20minimal%20workspace%20natural%20light&width=1920&height=400&seq=inquiry-hero-bg-01&orientation=landscape"
            alt="立即洽詢背景"
            className="w-full h-full object-cover object-center"
          />
          {/* 左右漸層主遮罩 */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d2210]/94 via-[#0d2210]/74 to-[#0d2210]/42" />
          {/* 上下輔助遮罩 */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d2210]/70 via-transparent to-[#0d2210]/28" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb
              items={[
                { label: '首頁', href: '/' },
                { label: '合作相關', href: '/cooperation' },
                { label: '立即洽詢' },
              ]}
              className="mb-4 text-gray-300"
            />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              合作相關
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              立即洽詢
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              留下您的需求，康永生技將協助您評估適合的 ODM / OEM 合作方式。
            </p>
          </div>
        </div>
      </section>

      {/* Suitable for */}
      <section className="py-14 bg-white border-b border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              適合對象
            </span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] mb-3">哪些品牌適合洽詢？</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto">
              無論您是剛起步的新品牌還是想拓展產品線的成熟品牌，康永都能提供對應方案
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {suitableFor.map((s) => (
              <div key={s.title} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-green-100 mb-4">
                  <i className={`${s.icon} text-[#4CAF50] text-xl`} />
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-2">{s.title}</h3>
                <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main content: form + contact info */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-start">

            {/* Left: contact info + LINE */}
            <div className="w-full lg:w-[320px] flex-shrink-0 space-y-5">
              <h2 className="text-[#1a2e1a] font-bold text-[1.5rem] mb-2">其他聯絡方式</h2>

              {/* LINE */}
              <div className="bg-[#e8f5e9] border border-[#c8e6c9] rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-line-fill text-[#4CAF50] text-xl" />
                  <p className="text-[#1a2e1a] font-bold text-[1rem]">LINE 官方帳號</p>
                </div>
                <p className="text-[#1a2e1a] text-[0.875rem] font-medium mb-1">@126uwznp 快速洽談</p>
                <p className="text-gray-600 text-[0.8125rem] leading-[1.75] mb-4">
                  加入康永生技 LINE 官方帳號，即時傳訊詢問，業務人員快速回覆。
                </p>
                <a
                  href="https://lin.ee/X7Hcpf4"
                  rel="nofollow"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.9375rem] px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer w-full"
                >
                  <i className="ri-line-fill text-lg" />
                  立即加入 LINE
                </a>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4 bg-white rounded-xl p-4 border border-gray-100">
                <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                  <i className="ri-phone-line text-[#4CAF50] text-lg" />
                </div>
                <div>
                  <p className="text-gray-400 text-[0.75rem] mb-0.5">聯絡電話</p>
                  <a href="tel:04-22029858" className="text-[#1a2e1a] font-semibold text-[0.9375rem] hover:text-[#4CAF50] transition-colors cursor-pointer">
                    (04)-22029858
                  </a>
                  <p className="text-gray-400 text-[0.75rem] mt-1">週一至週五 09:00–18:00</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4 bg-white rounded-xl p-4 border border-gray-100">
                <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                  <i className="ri-mail-line text-[#4CAF50] text-lg" />
                </div>
                <div>
                  <p className="text-gray-400 text-[0.75rem] mb-0.5">電子郵件</p>
                  <a href="mailto:natural1070606@gmail.com" className="text-[#1a2e1a] font-semibold text-[0.875rem] hover:text-[#4CAF50] transition-colors cursor-pointer break-all">
                    natural1070606@gmail.com
                  </a>
                </div>
              </div>

              {/* Process reminder */}
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-4 flex items-center gap-2">
                  <i className="ri-map-pin-time-line text-[#4CAF50]" />
                  洽詢後的流程
                </p>
                <div className="space-y-3">
                  {processReminders.map((r) => (
                    <div key={r.step} className="flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center bg-green-50 text-[#4CAF50] text-[0.6875rem] font-bold rounded-full flex-shrink-0 mt-0.5 border border-green-100">
                        {r.step}
                      </span>
                      <span className="text-gray-600 text-[0.8125rem] leading-[1.75]">{r.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: form */}
            <div className="w-full lg:flex-1">
              <h2 className="text-[#1a2e1a] font-bold text-[1.5rem] mb-6">填寫洽詢需求</h2>

              {submitState === 'success' ? (
                <div className="flex flex-col items-center text-center bg-white rounded-2xl border border-green-100 overflow-hidden">
                  {/* Success header */}
                  <div className="w-full px-8 pt-12 pb-8">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-5 border border-green-200 mx-auto">
                      <i className="ri-checkbox-circle-fill text-[#4CAF50] text-3xl" />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.375rem] mb-3">需求已送出，感謝您的洽詢！</h3>
                    <p className="text-gray-500 text-[0.9375rem] leading-[1.75] max-w-sm mx-auto">
                      康永生技將於 1 個工作天內與您聯繫，協助確認產品方向、劑型需求、預計數量與合作方式。
                    </p>
                  </div>

                  {/* LINE CTA strip */}
                  <div className="w-full bg-[#e8f5e9] border-t border-[#c8e6c9] px-6 sm:px-8 py-6">
                    <p className="text-[#1a2e1a] font-semibold text-[0.9375rem] mb-1">若您希望更快取得回覆</p>
                    <p className="text-gray-600 text-[0.875rem] leading-[1.75] mb-4">
                      也可以立即加入 LINE，由專人協助您確認需求。
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a
                        href="https://lin.ee/X7Hcpf4"
                        rel="nofollow"
                        className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer w-full sm:w-auto"
                      >
                        <i className="ri-line-fill text-lg" />
                        立即加入 LINE
                      </a>
                      <span className="text-[#4CAF50] text-[0.875rem] font-medium">LINE@ ID：@126uwznp</span>
                    </div>
                  </div>

                  {/* Re-submit */}
                  <div className="px-8 py-5">
                    <button
                      onClick={() => setSubmitState('idle')}
                      className="whitespace-nowrap border border-gray-300 text-gray-500 hover:border-[#4CAF50] hover:text-[#4CAF50] font-medium text-[0.875rem] px-6 py-2.5 rounded-full transition-all duration-300 cursor-pointer"
                    >
                      再次填寫
                    </button>
                  </div>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-2xl border border-gray-100 p-7 space-y-5"
                >
                  {/* Row 1: Name + Company */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        姓名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="contact_name"
                        type="text"
                        required
                        placeholder="請輸入您的姓名"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        公司 / 品牌名稱
                      </label>
                      <input
                        name="company_name"
                        type="text"
                        placeholder="請輸入公司或品牌名稱"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                  </div>

                  {/* Row 2: Phone + Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        聯絡電話 <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="phone"
                        type="tel"
                        required
                        placeholder="請輸入聯絡電話"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="email"
                        type="email"
                        required
                        placeholder="請輸入電子信箱"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                  </div>

                  {/* Product type */}
                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                      想開發的產品類型
                    </label>
                    <select
                      name="product_type"
                      defaultValue=""
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer"
                    >
                      <option value="" disabled>請選擇產品類型</option>
                      {productTypes.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                      預計數量
                    </label>
                    <select
                      name="quantity"
                      defaultValue=""
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer"
                    >
                      <option value="" disabled>請選擇預計數量</option>
                      {quantityOptions.map((q) => (
                        <option key={q} value={q}>{q}</option>
                      ))}
                    </select>
                  </div>

                  {/* Requirement */}
                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                      需求說明 <span className="text-gray-400 text-[0.75rem] font-normal">({charCount}/500)</span>
                    </label>
                    <textarea
                      name="requirement"
                      rows={5}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      placeholder="請簡述您的需求，例如：產品方向、功效訴求、目標族群、合作方式或其他補充說明"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors resize-none bg-white"
                    />
                    {charCount > 500 && (
                      <p className="text-red-500 text-[0.75rem] mt-1">需求說明不能超過 500 字</p>
                    )}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitState === 'loading' || charCount > 500}
                    className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] disabled:opacity-60 text-white font-bold text-[1rem] py-4 rounded-lg transition-all duration-300 cursor-pointer"
                  >
                    {submitState === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg" />
                        送出中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line text-lg" />
                        送出洽詢需求
                      </>
                    )}
                  </button>

                  {submitState === 'error' && (
                    <p className="text-red-500 text-[0.875rem] text-center">送出失敗，請稍後再試或直接致電我們。</p>
                  )}

                  <p className="text-gray-400 text-[0.8125rem] text-center leading-[1.75]">
                    送出表單即代表您同意依據隱私政策處理個人資訊，以提供您所請求的服務回覆。
                  </p>

                  {/* LINE quick CTA below form */}
                  <div className="border-t border-gray-100 pt-5 mt-1">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#f7faf7] rounded-xl px-5 py-4 border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-[#e8f5e9] rounded-full border border-[#c8e6c9] flex-shrink-0">
                          <i className="ri-line-fill text-[#4CAF50] text-lg" />
                        </div>
                        <div>
                          <p className="text-[#1a2e1a] font-semibold text-[0.875rem]">想更快確認需求？</p>
                          <p className="text-gray-500 text-[0.8125rem]">填寫後同步加入 LINE，LINE@ ID：<span className="text-[#4CAF50] font-medium">@126uwznp</span></p>
                        </div>
                      </div>
                      <a
                        href="https://lin.ee/X7Hcpf4"
                        rel="nofollow"
                        className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.875rem] px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0 w-full sm:w-auto"
                      >
                        <i className="ri-line-fill" />
                        立即加入 LINE
                      </a>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: '常見問題', desc: '品牌、起訂量、法規、通路…常見答疑全整理。', href: '/cooperation/faq' },
        { title: '代工服務', desc: '炱鋷配方、劑型選擇、包裝設計…深入了解代工服務內容。', href: '/services/hot-formulas' },
        { title: 'LINE 諮詢', desc: '直接加入 LINE 快速洽談，專人即時回覆。', href: 'https://lin.ee/X7Hcpf4', external: true },
      ]} />
    </>
  );
}
